import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  VStack,
  Image,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FaEnvelope } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const { requestPasswordReset } = useAuth();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!email) {
      setFormError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      setIsSuccess(true);
      toast({
        title: 'Reset Link Sent',
        description: 'Check your email for password reset instructions',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      let errorMessage = 'Failed to send reset link';
      
      if (error.response) {
        switch (error.response.status) {
          case 404:
            errorMessage = 'No account found with this email address';
            break;
          case 429:
            errorMessage = 'Too many requests. Please try again later';
            break;
          default:
            errorMessage = error.response.data?.message || 'An error occurred';
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="calc(100vh - 64px)"
      bg={useColorModeValue('gray.50', 'gray.900')}
      py={12}
    >
      <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', sm: '8' }}>
        <Stack spacing="8">
          <Stack spacing="6" align="center">
            <Image
              src="/logo.png"
              alt="Budget Brilliance"
              h="60px"
            />
            <Stack spacing="3" textAlign="center">
              <Heading size="lg" color="brand.500">
                Forgot your password?
              </Heading>
              <Text color="gray.500">
                Enter your email address and we'll send you instructions to reset your password
              </Text>
            </Stack>
          </Stack>

          <Box
            py={{ base: '0', sm: '8' }}
            px={{ base: '4', sm: '10' }}
            bg={bgColor}
            boxShadow="lg"
            borderRadius={{ base: 'none', sm: 'xl' }}
            borderWidth="1px"
            borderColor={borderColor}
          >
            {isSuccess ? (
              <VStack spacing={4}>
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  Reset link sent! Check your email for further instructions.
                </Alert>
                <Text mt={4}>
                  Didn't receive the email? Check your spam folder or{' '}
                  <ChakraLink
                    onClick={() => {
                      setIsSuccess(false);
                      setEmail('');
                    }}
                    color="brand.500"
                    fontWeight="semibold"
                    cursor="pointer"
                  >
                    try again
                  </ChakraLink>
                </Text>
              </VStack>
            ) : (
              <form onSubmit={handleSubmit}>
                <Stack spacing="6">
                  <FormControl isInvalid={!!formError}>
                    <FormLabel>Email address</FormLabel>
                    <InputGroup>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        disabled={isLoading}
                      />
                      <InputRightElement>
                        <FaEnvelope color="gray.300" />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{formError}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="brand"
                    size="lg"
                    fontSize="md"
                    isLoading={isLoading}
                    loadingText="Sending..."
                  >
                    Send Reset Link
                  </Button>
                </Stack>
              </form>
            )}
          </Box>

          <Text textAlign="center">
            <ChakraLink
              as={Link}
              to="/login"
              color="brand.500"
              fontWeight="semibold"
              _hover={{ textDecoration: 'underline' }}
            >
              Back to Login
            </ChakraLink>
          </Text>
        </Stack>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
